import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Mic, Users, Brain, BookOpen, Lightbulb, Clock, MapPin,
  MessageSquare, CheckCircle2, Eye, Shield, Zap, HelpCircle, ChevronRight
} from 'lucide-react';
import { Section, SectionTitle, AnimatedCard } from '@/components/Section';
import { Button } from '@/components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const stagger = (i) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.08 } });

// ── DATA ──

const outcomeCards = [
  { icon: Eye, title: 'Clarity Instead of Noise', body: 'A sharper understanding of what AI actually is, what it is not, and why that distinction matters.' },
  { icon: Shield, title: 'Confidence to Engage', body: 'A more grounded, less fearful way to approach AI, automation, and technological change.' },
  { icon: Lightbulb, title: 'Practical Use Cases', body: 'Real examples of how individuals, teams, creators, and businesses can apply these tools in meaningful ways.' },
  { icon: HelpCircle, title: 'A New Question', body: 'A shift from asking "What can AI do for me?" to asking "What can I do with AI?"' },
];

const themes = [
  {
    title: 'Use What You Got and Make a Lot',
    body: 'A philosophy of practical entrepreneurship — leveraging the resources, skills, and experience you already have to build something real. This talk challenges audiences to stop waiting for perfect conditions and start creating value with what\'s in front of them.',
    tags: ['Entrepreneurship', 'Resourcefulness', 'Execution', 'Mindset'],
    icon: Brain,
  },
  {
    title: 'The AI Whisperer Mindset',
    body: 'A perspective-shifting session on curiosity, better prompts, systems thinking, and how to interact with AI in ways that create better outcomes and stronger human agency.',
    tags: ['Critical Thinking', 'Prompt Strategy', 'Mindset', 'Adaptability'],
    icon: Lightbulb,
  },
  {
    title: 'Why Human Value Still Matters',
    body: 'An exploration of what remains distinctly human in an age of automation, abundance, content saturation, and accelerating machine capability.',
    tags: ['Human Potential', 'Leadership', 'Meaningful Work', 'Adaptation'],
    icon: Users,
  },
  {
    title: 'Publishing, Legacy, and the Future of Story',
    body: 'A grounded look at how AI is changing writing, publishing, authorship, and the preservation of personal and cultural narratives.',
    tags: ['Publishing Innovation', 'Digital Storytelling', 'Legacy', 'Content Strategy'],
    icon: BookOpen,
  },
];

const formats = [
  { title: 'Strategic Roundtable', subtitle: 'Advisory / Leadership Session', body: 'A focused conversation for leadership teams, boards, founders, or organizations exploring the practical implications of AI, publishing, systems design, entrepreneurship, and emerging technology.', icon: MessageSquare, image: '/assets/boardroom-presentation.jpg' },
  { title: 'Panel Conversation', subtitle: 'As Needed', body: 'Insightful, candid participation in moderated discussions on artificial intelligence, innovation, authorship, entrepreneurship, digital change, and the human consequences of technological acceleration.', icon: Users, image: '/assets/dais-panel-discussion.jpg' },
  { title: 'Signature Keynote', subtitle: '45\u201360 minutes', body: 'A high-impact keynote designed to challenge assumptions, reduce fear, and give audiences a practical framework for thinking differently about AI, human value, and the future of work.', icon: Mic, image: '/assets/full-stage-audience.jpg' },
];

const considerations = [
  'Event type',
  'Audience size',
  'Travel requirements',
  'Format and duration',
  'Advisory vs. keynote participation',
];

const idealAudiences = [
  'Entrepreneur and startup communities',
  'Business leadership teams',
  'Professional associations',
  'Publishing and author organizations',
  'Educational and innovation-focused events',
  'Community groups exploring technology and change',
  'Conferences seeking practical AI programming for non-technical audiences',
];

// ── SECTIONS ──

function HeroSection() {
  return (
    <Section>
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-sm text-primary font-medium uppercase tracking-widest mb-4 block">Speaking & Advisory</span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-3" data-testid="speaking-headline">
            R. Dale Brunet
          </h1>
          <h2 className="text-lg md:text-xl text-slate-300 font-medium mb-6">
            Speaker | Technologist | Author
          </h2>
          <p className="text-base text-slate-400 leading-relaxed mb-8 max-w-xl">
            R. Dale Brunet delivers practical, provocative, and deeply human presentations across artificial intelligence, entrepreneurship, publishing, systems thinking, and the future of human value. Known for his work as "The AI Whisperer," he helps audiences move from fear and confusion to clarity, confidence, and action.
          </p>
          <div className="flex flex-wrap gap-3 mb-3">
            <Button asChild className="bg-primary text-white hover:text-white hover:bg-primary/80 shadow-[0_4px_0_0_rgba(168,130,20,1)] hover:shadow-[0_2px_0_0_rgba(168,130,20,1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-100" data-testid="book-speaking-btn">
              <Link to="/contact">Request Engagement</Link>
            </Button>
            <Button asChild variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800" data-testid="view-profile-btn">
              <a href="#speaker-profile">View Speaker Profile</a>
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Available for keynote presentations, panels, strategic roundtables, and select advisory conversations.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }}>
          <img
            src="https://customer-assets.emergentagent.com/job_ai-architect-55/artifacts/sw7cidcp_R_Dale_Brunet_AI_Bodyshot_1.png"
            alt="R. Dale Brunet"
            className="rounded-xl w-full max-w-sm mx-auto"
          />
        </motion.div>
      </div>
    </Section>
  );
}

function SignaturePositioning() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
      <motion.p
        className="text-sm text-slate-400 italic max-w-2xl"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
      >
        His work spans business, technology, publishing, and human potential — most notably through his AI-focused perspective known as "The AI Whisperer."
      </motion.p>
    </div>
  );
}

function WhySection() {
  return (
    <Section dark id="why-this-matters">
      <div className="max-w-3xl mx-auto">
        <SectionTitle subtitle="Context">Why This Message Matters</SectionTitle>
        <motion.div {...fadeUp} className="space-y-5 text-slate-300 leading-relaxed">
          <p>
            Artificial intelligence is already reshaping how people work, decide, create, publish, and compete. Most audiences do not need another abstract explanation of the technology. They need a clearer way to understand what is changing, what still matters, and how to respond without surrendering their judgment, voice, or value. This perspective is especially relevant in conversations around artificial intelligence, where clarity is often replaced by noise.
          </p>
          <p>
            R. Dale Brunet brings more than technical experience to this conversation. He brings real-world perspective shaped by decades of building systems, launching ventures, writing books, and helping others make sense of disruption while it is happening. His approach is practical, candid, and grounded in what people can actually do next.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}

function OutcomesSection() {
  return (
    <Section id="audience-outcomes">
      <SectionTitle subtitle="Outcomes">What Audiences Walk Away With</SectionTitle>
      <motion.p {...fadeUp} className="text-slate-400 max-w-2xl mb-8 -mt-2">
        Every presentation is tailored to the audience, but the goal is always the same: leave people with practical clarity they can use immediately.
      </motion.p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {outcomeCards.map((c, i) => (
          <AnimatedCard key={c.title} delay={i * 0.08}>
            <c.icon className="w-8 h-8 text-primary mb-3" />
            <h3 className="text-base font-bold text-white mb-2">{c.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed">{c.body}</p>
          </AnimatedCard>
        ))}
      </div>
    </Section>
  );
}

function ThemesSection() {
  return (
    <Section dark id="speaking-themes">
      <SectionTitle subtitle="Topics">Speaking Themes</SectionTitle>
      <motion.p {...fadeUp} className="text-slate-400 max-w-2xl mb-8 -mt-2">
        Signature topics can be tailored for keynote presentations, leadership discussions, industry events, publishing audiences, and entrepreneur-focused communities, including sessions built around his AI-focused work as "The AI Whisperer."
      </motion.p>
      <div className="grid md:grid-cols-2 gap-5">
        {themes.map((t, i) => (
          <AnimatedCard key={t.title} delay={i * 0.08}>
            <div className="flex items-start gap-4 mb-4">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <t.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-1">{t.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{t.body}</p>
              </div>
            </div>
            <div className="ml-[60px] flex flex-wrap gap-2">
              {t.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-slate-800 text-slate-400 border border-border/20">{tag}</span>
              ))}
            </div>
          </AnimatedCard>
        ))}
      </div>
    </Section>
  );
}

function AIWhispererBridge() {
  return (
    <Section id="ai-whisperer-bridge">
      <div className="max-w-2xl mx-auto text-center">
        <SectionTitle subtitle="Signature Perspective" className="text-center">Explore The AI Whisperer</SectionTitle>
        <motion.p {...fadeUp} className="text-slate-400 leading-relaxed mb-6 -mt-2">
          For audiences specifically interested in artificial intelligence, R. Dale Brunet's work as "The AI Whisperer" offers a deeper look at how to think differently, interact more effectively, and maintain human clarity in an increasingly automated world.
        </motion.p>
        <motion.div {...fadeUp}>
          <Button asChild variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800">
            <Link to="/ai-whisperer">Learn More About The AI Whisperer <ChevronRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </motion.div>
      </div>
    </Section>
  );
}

function FormatsSection() {
  return (
    <Section id="engagement-formats">
      <SectionTitle subtitle="Formats">Engagement Formats</SectionTitle>
      <div className="grid md:grid-cols-3 gap-5">
        {formats.map((f, i) => (
          <motion.div
            key={f.title}
            className="bg-card border border-border/30 rounded-xl overflow-hidden"
            {...stagger(i)}
            data-testid={`format-${i}`}
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={f.image} alt={f.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">{f.title}</h3>
              <span className="text-sm text-primary mb-3 block">{f.subtitle}</span>
              <p className="text-sm text-slate-400 leading-relaxed">{f.body}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function ConsiderationsSection() {
  return (
    <Section dark id="engagement-considerations">
      <SectionTitle subtitle="Details">Engagement Considerations</SectionTitle>
      <div className="grid lg:grid-cols-3 gap-8">
        <motion.div className="lg:col-span-2" {...fadeUp}>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>Speaking and advisory engagements are customized based on the audience, format, event goals, location, and level of participation required.</p>
            <p>For events outside the Colorado Springs region, hosts typically cover standard travel-related expenses such as transportation, lodging, and meals when applicable.</p>
            <p>A limited number of local entrepreneurial, nonprofit, educational, or community-based engagements may be considered with flexible arrangements when schedules permit.</p>
            <p>Organizations interested in keynote presentations, panels, strategic roundtables, or advisory participation are encouraged to reach out with event details, audience profile, and desired outcomes.</p>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
          <div className="bg-card border border-border/30 rounded-xl p-6">
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">Typical Considerations</h4>
            <ul className="space-y-3">
              {considerations.map((item) => (
                <li key={item} className="flex items-center gap-3 text-slate-300 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function SpeakerProfileSection() {
  return (
    <Section id="speaker-profile">
      <SectionTitle subtitle="Biography">Speaker Profile</SectionTitle>
      <div className="grid lg:grid-cols-3 gap-8">
        <motion.div className="lg:col-span-2" {...fadeUp}>
          <div className="space-y-5 text-slate-300 leading-relaxed">
            <p>R. Dale Brunet brings more than four decades of experience across technology, entrepreneurship, publishing, and systems development to every stage and advisory conversation.</p>
            <p>His perspective was shaped long before artificial intelligence became mainstream. He has built software, designed systems, launched ventures, written and published books, and spent years helping people make sense of rapidly changing technology in practical terms.</p>
            <p>That combination gives his presentations a distinctive tone: thoughtful, candid, grounded, and usable. He does not speak from abstraction. He speaks from lived experience, hard-earned perspective, and a deep interest in how human beings can remain relevant, effective, and fully engaged in a changing world.</p>
            <p>Whether speaking to business groups, entrepreneurs, creators, leadership teams, or conference audiences, R. Dale brings a systems-oriented lens focused on what matters most: what can be understood, what can be applied, and what still belongs uniquely to us.</p>
          </div>
        </motion.div>
        <motion.div className="space-y-5" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
          <img
            src="https://customer-assets.emergentagent.com/job_d94f9753-7cd9-433d-b7b2-74888a26953a/artifacts/zjadlivq_R_Dale_Brunet_AI_4.jpg"
            alt="R. Dale Brunet"
            className="rounded-xl w-full object-cover"
          />
          <div className="bg-card border border-border/30 rounded-xl p-6">
            <h4 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">Quick Facts</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-300 text-sm"><Clock className="w-4 h-4 text-primary" /> 40+ years technology experience</li>
              <li className="flex items-center gap-3 text-slate-300 text-sm"><Lightbulb className="w-4 h-4 text-primary" /> Multiple active ventures</li>
              <li className="flex items-center gap-3 text-slate-300 text-sm"><BookOpen className="w-4 h-4 text-primary" /> 40+ published titles</li>
              <li className="flex items-center gap-3 text-slate-300 text-sm"><MapPin className="w-4 h-4 text-primary" /> Based in Colorado Springs</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function IdealAudiencesSection() {
  return (
    <Section dark id="ideal-audiences">
      <div className="max-w-3xl mx-auto">
        <SectionTitle subtitle="Who It's For">Ideal Audiences</SectionTitle>
        <motion.p {...fadeUp} className="text-slate-400 mb-6 -mt-2">
          R. Dale Brunet's presentations are especially well suited for organizations and communities navigating change, complexity, and the growing presence of AI in everyday work and decision-making.
        </motion.p>
        <ul className="space-y-3 mb-6">
          {idealAudiences.map((a, i) => (
            <motion.li key={a} {...stagger(i)} className="flex items-start gap-3 text-slate-300">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-sm">{a}</span>
            </motion.li>
          ))}
        </ul>
        <motion.p {...fadeUp} className="text-sm text-primary/80 font-medium italic">
          Especially valuable for audiences who are curious, cautious, skeptical, or simply trying to understand what comes next.
        </motion.p>
      </div>
    </Section>
  );
}

function FinalCTASection() {
  return (
    <Section id="final-cta">
      <div className="text-center max-w-3xl mx-auto">
        <motion.h2 {...fadeUp} className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="speaking-cta-headline">
          Bring The AI Whisperer to Your Audience
        </motion.h2>
        <motion.div {...fadeUp} className="space-y-4 text-slate-400 leading-relaxed mb-8">
          <p>If you are looking for a speaker who can make complex ideas more understandable, more relevant, and more human for your audience, let's start the conversation.</p>
          <p>Whether the focus is artificial intelligence, business, publishing, or the broader question of how people adapt in a changing world, the right format can be tailored to your event, audience, and goals.</p>
        </motion.div>
        <motion.div {...fadeUp} className="flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="bg-primary text-white hover:text-white hover:bg-primary/80 shadow-[0_4px_0_0_rgba(168,130,20,1)] hover:shadow-[0_2px_0_0_rgba(168,130,20,1)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all duration-100" data-testid="inquire-speaking-btn">
            <Link to="/contact">Request Engagement <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-800">
            <Link to="/schedule">Schedule a Conversation <ChevronRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </motion.div>
      </div>
    </Section>
  );
}

// ── MAIN PAGE ──

export default function SpeakingPage() {
  useEffect(() => {
    document.title = 'R. Dale Brunet | The AI Whisperer | Speaking & Advisory';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Book R. Dale Brunet for keynote presentations, panels, strategic roundtables, and advisory conversations on AI, entrepreneurship, publishing, systems thinking, and human value.');
    return () => { document.title = 'R. Dale Brunet | Technology Architect, AI Strategist, Founder, Author'; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="pt-20">
      <HeroSection />
      <FormatsSection />
      <SignaturePositioning />
      <ThemesSection />
      <AIWhispererBridge />
      <IdealAudiencesSection />
      <OutcomesSection />
      <ConsiderationsSection />
      <SpeakerProfileSection />
      <FinalCTASection />
    </main>
  );
}
